<script lang="ts" setup>
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { onMounted, type Ref, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import VocabService from "@/common/api/vocab.service";
import type { ClassQuiz } from "@/common/types/vocab";

import ClassQuizBaseLayout from "./ClassQuizBaseLayout.vue";
import QuizJumbotron from "./QuizJumbotron.vue";

const props = defineProps<{
  classQuiz: ClassQuiz;
}>();
const { t } = useI18n();
const router = useRouter();

interface StatsRow {
  name: string;
  answers: {
    total: number;
    correct: number;
    wrong: number;
  };
}

const stats: Ref<StatsRow[]> = ref([]);

onMounted(
  async () => (stats.value = (await VocabService.getClassQuizStats(props.classQuiz.code)).data.data)
);

const blurStats = ref<boolean>(true);
const onShowStats = () => (blurStats.value = false);

const onEndQuiz = () => router.push({ name: "vocab.home" });
</script>

<template>
  <ClassQuizBaseLayout fixed-height>
    <template #jumbotron>
      <QuizJumbotron class="stats-jumbotron">
        <AbcHeading tag="h1" size="lg" class="text-center">
          {{ t("vocab.classQuiz.stats.statistics") }}
        </AbcHeading>
      </QuizJumbotron>
    </template>

    <div class="stats container" :class="{ 'stats--blur': blurStats }">
      <table class="stats__table" :inert="blurStats">
        <thead>
          <tr>
            <th>{{ t("vocab.classQuiz.stats.student") }}</th>
            <th>{{ t("vocab.classQuiz.stats.answers") }}</th>
            <th>{{ t("vocab.classQuiz.stats.correct") }}</th>
            <th>{{ t("vocab.classQuiz.stats.wrong") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in stats" :key="index">
            <td>{{ row.name }}</td>
            <td>{{ row.answers.total }}</td>
            <td>{{ row.answers.correct }}</td>
            <td>{{ row.answers.wrong }}</td>
          </tr>
        </tbody>
      </table>
      <TgButton
        v-if="blurStats"
        class="stats__show-button"
        color="secondary"
        @click="onShowStats()"
      >
        {{ t("vocab.classQuiz.stats.showStats") }}
      </TgButton>
    </div>

    <template #footer>
      <div class="container text-center">
        <TgButton size="lg" @click="onEndQuiz()">
          {{ t("vocab.classQuiz.end.finishQuiz") }}
        </TgButton>
      </div>
    </template>
  </ClassQuizBaseLayout>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.stats-jumbotron {
  padding: 38px 0 24px 0;
}

.stats {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 100%;
  overflow: auto;

  &--blur {
    overflow: hidden;
  }
}

.stats__table {
  width: 100%;
  border-spacing: 0;
  border-collapse: separate;
  transition: filter 50ms ease-out;

  &[inert] {
    filter: blur(8px);
    padding: 5px;
  }

  th,
  td {
    padding: 8px 10px;
    border: 1px solid rgb(@theme-colors[@grey][50]);
  }

  th {
    @border: 2px solid rgb(@theme-colors[@grey][50]);

    position: sticky;
    z-index: 1;
    top: 0;
    border-top: @border;
    background-color: @colors[@white];

    font-weight: 500;

    &:first-child {
      border-left: @border;
    }

    &:last-child {
      border-right: @border;
    }
  }

  td {
    background-color: rgb(@theme-colors[@grey][10]);
  }
}

.stats__show-button {
  position: absolute;
  align-self: center;
}
</style>
